import { createStore } from 'react-sweet-state';
import { produce } from 'immer';
import { cloneDeep } from 'lodash';

import { contentTypeFromBriefType } from 'src/data/brief';
import {
  actualizeGenerationTool,
  doesToolSupportsSubTemplates,
  GENERATION_TOOL,
  GENERATION_TOOL_GROUP,
  getSuggestToolsByGroup,
  getToolBriefType,
  isBasicContentImproverTool,
  isFreestyleGenerationTool,
  isRewriteGenerationTool,
  mapGenerationTool,
} from 'src/data/generationTool';
import { getProjectLastUsedGenerationInfo } from 'src/data/project';
import { PROMPT_SUB_TEMPLATE_ID } from 'src/data/template';
import { getQueryParamValue } from 'src/lib/browser';
import { ROUTE_PARAMS } from 'src/routes';

import { DISPLAY_MODE, SIDEBAR_GENERIC_ITEM } from '../../const';
import { getCustomMultiPromptGroupQueryParam, getSidebarItemFromGenerationTool } from '../../utils';

const INITIAL_STATE = {
  project: null,
  generationTool: null,
  subTemplateId: null,
  freestyleToolId: null,
  customMultiPromptId: null,

  sidebarSelectedItem: null,
  currentDisplayMode: null,
  popupsQueue: [],
  currentVisiblePopup: null,

  improveStructureInstructionsMap: {},

  ratingsCount: {},
};

export const PRIVATE_ACTIONS = {
  init:
    () =>
    ({ setState, dispatch }, { project }) => {
      setState({
        ...cloneDeep(INITIAL_STATE),
        project,
      });

      dispatch(PRIVATE_ACTIONS.initGenerationTool());
      dispatch(PRIVATE_ACTIONS.initSubTemplateId());
      dispatch(PRIVATE_ACTIONS.initSidebarSelection());
    },

  update:
    () =>
    ({ setState }, { project }) => {
      if (!project) {
        return;
      }

      setState({ project });
    },

  initGenerationTool:
    () =>
    ({ setState }, { project, generationTool }) => {
      // Selected generation tool might be passed from outside
      const generationToolFromUrl = getQueryParamValue(ROUTE_PARAMS.generationTool);
      const generationToolToUse = generationTool || generationToolFromUrl;
      const actualizedTool = actualizeGenerationTool(generationToolToUse);
      if (actualizedTool) {
        setState({ generationTool: actualizedTool });
        return;
      }

      // Also, generation tool can be read from previous generations
      const lastUsedInfo = getProjectLastUsedGenerationInfo(project);

      if (lastUsedInfo) {
        const supportsSubTemplate = doesToolSupportsSubTemplates(lastUsedInfo.generationTool);

        setState({
          generationTool: lastUsedInfo.generationTool,
          customMultiPromptId: lastUsedInfo.customMultiPromptId,
          subTemplateId:
            lastUsedInfo.subTemplateId || (supportsSubTemplate ? PROMPT_SUB_TEMPLATE_ID : null),
        });
        return;
      }

      // We can always fall back to default value
      setState({ generationTool: actualizedTool });
    },

  initSidebarSelection:
    () =>
    ({ getState, setState }) => {
      const { generationTool } = getState();

      // Check for tool-category URL param first (allows opening a category without selecting a tool)
      const toolCategoryFromUrl = getQueryParamValue(ROUTE_PARAMS.toolCategory);
      if (
        toolCategoryFromUrl &&
        Object.values(GENERATION_TOOL_GROUP).includes(toolCategoryFromUrl)
      ) {
        return setState({ sidebarSelectedItem: toolCategoryFromUrl });
      }

      if (generationTool === GENERATION_TOOL.customMultiPrompt) {
        const customMultiPromptGroup = getCustomMultiPromptGroupQueryParam();
        if (customMultiPromptGroup) {
          return setState({ sidebarSelectedItem: customMultiPromptGroup });
        }
      }
      const sidebarItem = generationTool
        ? getSidebarItemFromGenerationTool(generationTool)
        : SIDEBAR_GENERIC_ITEM.recentlyUsed;
      setState({ sidebarSelectedItem: sidebarItem });
    },

  alignSidebarSelection:
    () =>
    ({ getState, setState }) => {
      const { generationTool } = getState();
      if (isFreestyleGenerationTool(generationTool)) {
        setState({ sidebarSelectedItem: GENERATION_TOOL_GROUP.anyCopy });
      } else if (
        isRewriteGenerationTool(generationTool) ||
        isBasicContentImproverTool(generationTool)
      ) {
        setState({ sidebarSelectedItem: GENERATION_TOOL_GROUP.boost });
      }
    },

  initSubTemplateId:
    () =>
    ({ setState }) => {
      const subTemplateIdFromUrl = getQueryParamValue(ROUTE_PARAMS.subTemplateId);
      if (!subTemplateIdFromUrl) return;

      setState({ subTemplateId: subTemplateIdFromUrl });
    },
};

const ACTIONS = {
  getProject:
    () =>
    ({ getState }) => {
      return getState().project;
    },

  getGenerationTool:
    () =>
    ({ getState }) => {
      return getState().generationTool;
    },
  setGenerationTool:
    (generationTool) =>
    ({ setState, dispatch }) => {
      const tool = mapGenerationTool(generationTool);
      setState({ generationTool: tool });

      dispatch(PRIVATE_ACTIONS.alignSidebarSelection());
    },
  setCustomMultiPromptId:
    (id) =>
    ({ setState }) => {
      setState({ customMultiPromptId: id });
    },
  getCustomMultiPromptId:
    () =>
    ({ getState }) => {
      return getState().customMultiPromptId;
    },
  getBriefType:
    () =>
    ({ getState }) => {
      const { generationTool } = getState();
      return getToolBriefType(generationTool);
    },
  getContentType:
    () =>
    ({ dispatch }) => {
      const briefType = dispatch(ACTIONS.getBriefType());
      return contentTypeFromBriefType(briefType);
    },

  activateFreestyleTool:
    (freestyleToolId, generationTool) =>
    ({ setState }) => {
      setState({
        generationTool: generationTool || GENERATION_TOOL.freestyle,
        freestyleToolId,
      });
    },

  selectSidebarItem:
    (item) =>
    ({ setState }) => {
      // if group has only one tool, select it (but in case of "customMultiPrompt" group, there might be additional tools loaded from BE)
      let newTool = null;
      const groupTools = getSuggestToolsByGroup(item);
      if (groupTools.length === 1 && item !== GENERATION_TOOL_GROUP.customMultiPrompt) {
        newTool = groupTools[0];
      }

      setState({
        sidebarSelectedItem: item,
        generationTool: newTool,
        subTemplateId: null,
      });
    },

  activateDisplayMode:
    (mode) =>
    ({ setState }) => {
      setState({ currentDisplayMode: mode });
    },

  activateListView:
    () =>
    ({ dispatch }) => {
      dispatch(ACTIONS.activateDisplayMode(DISPLAY_MODE.list));
    },

  isDocumentViewActive:
    () =>
    ({ getState }) => {
      const { currentDisplayMode } = getState();
      return currentDisplayMode === DISPLAY_MODE.document;
    },

  getDisplayMode:
    () =>
    ({ getState }) => {
      return getState().currentDisplayMode;
    },

  setRatingsCount:
    (ratingsCount) =>
    ({ setState, getState }) => {
      if (!ratingsCount) {
        return;
      }

      const currentRatingsCount = getState().ratingsCount;
      const updatedAssetRatings = ratingsCount[Object.keys(ratingsCount)[0]];
      // update all tools that have the same asset types
      Object.keys(currentRatingsCount).map((key) => {
        const currentAssetKeys = Object.keys(currentRatingsCount[key]);
        currentAssetKeys.forEach((assetKey) => {
          if (updatedAssetRatings[assetKey]) {
            currentRatingsCount[key][assetKey] = updatedAssetRatings[assetKey];
          }
        });
        return currentRatingsCount;
      });
      setState({ ratingsCount: { ...currentRatingsCount, ...ratingsCount } });
    },

  getImproveStructureInstructionsData:
    (generationResultId, assetType) =>
    ({ getState }) => {
      const key = improveParamsMapKey(generationResultId, assetType);
      return getState().improveStructureInstructionsMap[key];
    },

  setImproveStructureInstructionsData:
    (generationResultId, assetType, newData) =>
    ({ getState, setState }) => {
      const { improveStructureInstructionsMap } = getState();

      const key = improveParamsMapKey(generationResultId, assetType);
      const newMap = produce(improveStructureInstructionsMap, (draft) => {
        draft[key] = newData;
      });

      setState({ improveStructureInstructionsMap: newMap });
    },

  isSubTemplateSelected:
    () =>
    ({ getState }) => {
      const { subTemplateId } = getState();

      // When prompt is selected
      if (subTemplateId === PROMPT_SUB_TEMPLATE_ID) {
        return false;
      }

      return Boolean(subTemplateId);
    },

  setSubTemplateId:
    (id) =>
    ({ setState }) => {
      setState({ subTemplateId: id });
    },
};

export const MainStore = createStore({
  initialState: INITIAL_STATE,
  actions: ACTIONS,
  name: 'document-view-main',
});

const improveParamsMapKey = (generationResultId, assetType) => `${generationResultId}-${assetType}`;
